import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ProfileSidebar from '../../components/Profile/ProfileSidebar';
import ProfileMobileSubNav from '../../components/Profile/ProfileMobileSubNav';
import { FaHome, FaMapMarkerAlt, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { GoArrowLeft } from 'react-icons/go';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { GiPlainCircle } from 'react-icons/gi';
import log from '../../utils/logger';
import { DEFAULT_CENTER } from '../../config/appConfig';
import api from '../../api/api';
import BuyerConfirmationAnimation from '../../components/BuyerConfirmationAnimation';


const homeIcon = L.icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/1946/1946436.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const factoryIcon = L.icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/708/708179.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const getCurvedPoints = (from, to, offset = 0.02, numPoints = 5) => {
  const lat1 = from[0], lng1 = from[1];
  const lat2 = to[0], lng2 = to[1];

  // Midpoint between start and end
  const latMid = (lat1 + lat2) / 2;
  const lngMid = (lng1 + lng2) / 2;

  // Vector from start to end
  const dx = lng2 - lng1;
  const dy = lat2 - lat1;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Perpendicular offset for control point
  const offsetLat = latMid + offset * (dx / dist);
  const offsetLng = lngMid - offset * (dy / dist);

  // Quadratic Bezier formula: B(t) = (1-t)^2 * P0 + 2(1-t)t * P1 + t^2 * P2
  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const lat = (1 - t) ** 2 * lat1 + 2 * (1 - t) * t * offsetLat + t ** 2 * lat2;
    const lng = (1 - t) ** 2 * lng1 + 2 * (1 - t) * t * offsetLng + t ** 2 * lng2;
    points.push([lat, lng]);
  }
  return points;
};


const MapLayer = ({ home, supplier }) => {
  const map = useMap();

  useEffect(() => {
    map.fitBounds([home, supplier], { padding: [50, 50] });
  }, [home, supplier, map]);

  const curvedPoints = getCurvedPoints(home, supplier);

  return (
    <>
      <Marker position={home} icon={homeIcon} />
      <Marker position={supplier} icon={factoryIcon} />
      <Polyline
        positions={curvedPoints}
        pathOptions={{
          color: 'black',
          dashArray: '4, 8',
          weight: 2,
          opacity: 0.8,
        }}
      />
    </>
  );
};

const TrackOrder = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const navigationLocation = useLocation();
  const {
    order: stateOrder,
    orderNumber: stateOrderNumber,
    timestamp: stateTimestamp,
    store: stateStore,
  } = navigationLocation.state || {};
  const [order, setOrder] = useState(stateOrder || null);
  const [store, setStore] = useState(stateStore || null);
  const [orderNumber, setOrderNumber] = useState(stateOrderNumber || null);
  const [timestamp, setTimestamp] = useState(stateTimestamp || null);
  const { user, idToken, logout } = useUser();
  const { clearCart } = useCart();
  const [showHistory, setShowHistory] = useState(false);
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const [showConfirmation, setShowConfirmation] = useState(
    Boolean(navigationLocation.state?.justPurchased),
  );
  const handleConfirmationComplete = useCallback(() => setShowConfirmation(false), []);
  const [loadError, setLoadError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!navigationLocation.state?.justPurchased) { return; }
    // Strip the one-shot 'justPurchased' flag from history state so it doesn't
    // fire again on a back/forward; we rebuild state without that key.
    const { justPurchased: _justPurchased, ...rest } = navigationLocation.state;
    navigate(navigationLocation.pathname + navigationLocation.search, {
      replace: true,
      state: rest,
    });
  }, []);

  useEffect(() => {
    if (order) { return; }          // already came from navigation state
    if (!orderId) { return; }       // nothing to fetch

    if (!idToken) {
      setLoadError('AUTH_REQUIRED');
      return;
    }

    const fetchOrder = async () => {
      setIsFetching(true);
      try {
        const res = await api.get(`/buyer/order/getOrder/${orderId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });

        setOrder(res.data.response.data);
        setStore(res.data.response.data.store);
        setOrderNumber(res.data.response.data.orderNumber);
        setTimestamp(res.data.response.data.createdAt);
        setLoadError(null);
      } catch (err) {
        log.error('TrackOrder fetch failed', err);
        const message = err.response?.data?.error || '';
        if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
          navigate('/home');
          sessionStorage.setItem('sessionExpired', 'true');
          sessionStorage.setItem(
            'toast',
            JSON.stringify({
              message: t('sessionExpiredMessage'),
              type: 'error',
            }),
          );
          logout();
          clearCart();
        }
        const status = err?.response?.status;
        if (status === 401 || status === 400) {
          setLoadError('AUTH_REQUIRED');
        } else if (status === 404) {
          setLoadError('NOT_FOUND');
        } else {
          setLoadError('GENERIC');
        }
      } finally {
        setIsFetching(false);
      }
    };

    fetchOrder();
  }, [order, orderId, idToken]);

  const statusOrder = ['pending', 'accepted', 'ready', 'collected', 'completed'];

  const getBarState = (idx, currentStatus) => {
    const currentIndex = statusOrder.indexOf(currentStatus);
    if (idx < currentIndex) {return 'done';}
    if (idx === currentIndex) {return 'active';}
    return 'todo';
  };

  const userLocation = order?.address?.latitude != null && order?.address?.longitude != null
    ? [order.address.latitude, order.address.longitude]
    : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng];

  const supplierLocation = store?.location?.latitude != null && store?.location?.longitude != null
    ? [store.location.latitude, store.location.longitude]
    : [45.4501, -73.4658];

  const formatTime = date =>
    new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDate = date =>
    new Date(date).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });

  const formatMoney = (value) => Number(value || 0).toFixed(2);

  const couponDiscount = Array.isArray(order?.discountedItems)
    ? order.discountedItems.reduce((sum, item) => sum + Number(item?.discount || 0), 0)
    : 0;
  const subtotalBeforeDiscount = Number(order?.subtotal || 0);
  const subtotalAfterDiscount = Number(
    order?.subtotalAfterDiscount ?? Math.max(0, subtotalBeforeDiscount - couponDiscount),
  );
  const totalTaxes = Number(order?.taxAmount || 0);
  const grandTotal = Number(order?.grandTotal ?? 0);

  if (!order || !order.address || !store || !store.location) {
    const isAuthError = loadError === 'AUTH_REQUIRED';
    const isNotFound = loadError === 'NOT_FOUND';
    const isLoading = isFetching || (orderId && !loadError);

    return (
      <>
        <Header />
        <ProfileMobileSubNav />
        <section className="lg:pt-20 lg:pb-20 bg-white text-black font-sans">
          <div className="max-w-[1220px] w-full mx-auto flex gap-[70px]">
            {/* Sidebar */}
            <div className="hidden lg:block">
              <ProfileSidebar />
            </div>

            <div className="min-h-dvh flex-1 flex flex-col items-center justify-center text-center px-4 gap-4">
              {isLoading ? (
                <>
                  <div className="h-8 w-8 border-4 border-gray-200 border-t-black rounded-full animate-spin" aria-hidden="true" />
                  <p className="text-sm text-gray-600">{t('loading', { defaultValue: 'Loading...' })}</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold text-gray-900">
                    {isAuthError ? t('orderAuthRequiredTitle') : isNotFound ? t('orderNotFoundTitle') : t('orderLoadError')}
                  </p>
                  {(isAuthError || isNotFound) && (
                    <p className="text-sm text-gray-600 max-w-md">
                      {isAuthError ? t('orderAuthRequiredBody') : t('orderNotFoundBody')}
                    </p>
                  )}
                  {isAuthError ? (
                    <button
                      onClick={() => navigate('/home')}
                      className="h-11 px-5 rounded-md bg-black text-white text-sm font-semibold hover:opacity-90"
                    >
                      {t('signIn')}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/home')}
                      className="h-11 px-5 rounded-md border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                    >
                      {t('backToHome')}
                    </button>
                  )}
                </>
              )}
            </div>

          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      {showConfirmation && (
        <BuyerConfirmationAnimation onComplete={handleConfirmationComplete} />
      )}
      <div className="flex flex-col min-h-dvh">
        <Header />
        <ProfileMobileSubNav />
        <section className="lg:pt-20 lg:pb-20 bg-white">
          <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
            {/* Sidebar */}
            {(idToken && user) && (
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
            )}

            {/* Main content */}
            <div className="flex-1 lg:rounded-2xl border border-[#E6E6E6]">

              {/* Header */}
              <div className="p-4 space-y-6">
                <header className="flex items-center space-x-3 text-sm">
                  <button
                    aria-label="Retour"
                    onClick={() => {
                      (idToken && user) ? navigate('/profile/orders') : navigate('/home');
                    }}
                    className="focus:outline-none text-[25px]
                              p-2 rounded-lg
                              hover:bg-gray-200
                              transition-colors"
                  >
                    <GoArrowLeft />
                  </button>
                  <span className="flex-1 text-center font-semibold">{store?.storeName}</span>
                  <span className="w-6" />
                </header>

                {/* Status */}
                <h2 className="text-center font-semibold text-base">{t('Your order is')} {t(order?.orderStatus || 'pending')}!</h2>

                {/* Progress bar */}
                <div className="flex space-x-2 pt-1 pb-1">
                  {statusOrder.map((stat, idx) => {
                    const state = getBarState(idx, order?.orderStatus || 'pending');
                    return (
                      <div key={stat} className="relative w-1/5 h-[4.2px] overflow-hidden bg-[#eeeeee]">
                        <div
                          className={`
                            absolute inset-0
                            ${state === 'done' ? 'bg-green-600' : ''}
                            ${state === 'active' ? 'bg-green-600 animate-fill-bar' : ''}
                            ${state === 'todo' ? 'bg-[#eeeeee]' : ''}
                          `}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Map */}
              <div className="overflow-hidden border border-gray-200 h-[300px] sm:h-[400px] md:h-[480px] lg:h-[600px]">
                <MapContainer
                  center={userLocation}
                  zoom={13}
                  className="w-full h-full z-0"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <MapLayer home={userLocation} supplier={supplierLocation} />
                </MapContainer>
              </div>

              {/* Location info block */}
              {/* <section className="bg-white border border-gray-200 rounded-lg p-4 text-sm">
                <div className="flex space-x-4">
                  <div className="relative flex flex-col items-center" style={{ width: 36 }}>
                    <div className="bg-black text-white rounded-md p-2 flex items-center justify-center z-10" style={{ width: 36, height: 36 }}>
                      <FaMapMarkerAlt className="text-lg" />
                    </div>
                    <div className="w-px bg-black" style={{ height: 28 }} />
                    <div className="bg-black text-white rounded-md p-2 flex items-center justify-center z-10" style={{ width: 36, height: 36 }}>
                      <FaHome className="text-lg" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 py-1">
                    <div className="mb-2 -mt-1">
                      <p className="font-semibold text-black">{store?.storeName}</p>
                    </div>
                    <div className="mt-8">
                      <p className="font-semibold text-black">{user?.firstName} {user?.lastName}</p>
                      <p className="text-[#666] text-xs">
                        {order.address.addressType}, {order.address.formattedAddress}
                      </p>
                    </div>
                  </div>
                </div>
              </section> */}
              <div className="p-4 space-y-6 bg-gray-300">
                <section className="relative bg-white border border-gray-200 rounded-lg p-4 text-sm">
                  {/* Toggle */}
                  <button
                    onClick={() => setShowHistory(v => !v)}
                    aria-label="Toggle order history"
                    className="
                      absolute right-4 top-4
                      text-gray-600 hover:text-black
                      p-3 rounded-full
                      hover:bg-gray-200
                      active:bg-[#aaa]
                      transition-all duration-200
                    "
                  >
                    {showHistory ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  <div className="flex gap-4">
                    {/* ================= LEFT TIMELINE COLUMN ================= */}
                    <div className="flex flex-col items-center w-[36px]">
                      {/* Merchant icon */}
                      <div className="bg-black text-white rounded-md w-[36px] h-[36px] flex items-center justify-center z-10">
                        <FaMapMarkerAlt />
                      </div>

                      {/* Expanded timeline */}
                      {showHistory ? (
                        order.statusHistory.map((item, index) => {
                          const isFirst = index === 0;
                          const isCanceled = item.status === 'canceled';
                          const isLast = index === order.statusHistory.length - 1;

                          return (
                            <div key={index} className={`flex flex-wrap w-12 ${isFirst? 'min-h-[45px]': 'min-h-[65px]'} px-2 text-center`}>
                              {/* Connector */}
                              <div className='ml-[14px] w-[1px] border-l-2 border-black h-full z-40' />

                              {/* Status circle */}
                              <div className='mr-8 min-w-[30px] w-[30px] z-50'>
                                {isLast && (
                                  <>
                                    <span className={`${isCanceled? 'bg-[#e00000]': 'bg-[#00c000]'} flex items-center justify-center ml-[5px] absolute w-[20px] h-[20px] rounded-full opacity-50 animate-pulse-aura`} />
                                    <span className={`${isCanceled? 'bg-[#e00000]': 'bg-[#00c000]'} flex items-center justify-center mt-[1px] ml-[7px] absolute w-[16px] h-[16px] rounded-full opacity-50 animate-pulse-aura`} />
                                  </>
                                )}
                                <span className={`${isCanceled? 'text-[#e00000]': 'text-[#00c000]'} w-[30px] flex items-center justify-center`}>
                                  <GiPlainCircle size={18} />
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        /* Collapsed connector */
                        <div className="w-[1px] border-l-2 border-black h-7 z-40" />
                      )}

                      {showHistory && (
                        <div className='flex flex-wrap w-12 min-h-[65px] px-2 text-center'>
                          <div className='ml-[14px] w-[1px] border-l-2 border-black h-[65px] z-40' />
                        </div>)}

                      {/* Customer icon */}
                      <div className="bg-black text-white rounded-md w-[36px] h-[36px] flex items-center justify-center z-10">
                        <FaHome />
                      </div>
                    </div>

                    {/* ================= RIGHT TEXT COLUMN ================= */}
                    <div className="flex flex-col flex-1">
                      {/* Store header */}
                      <div className={`${showHistory? 'min-h-[45px]' : ''}`}>
                        <p className="font-semibold text-black">{store?.storeName}</p>
                      </div>

                      {/* Expanded history text */}
                      {showHistory ? (
                        <div className="flex flex-col mt-4">
                          {order.statusHistory.map((item, index) => (
                            <div
                              key={index}
                              className='min-h-[65px] flex flex-col justify-center'
                            >
                              <p className="font-semibold capitalize">{t(item.status)}</p>
                              <p className="text-xs text-gray-600">
                                {formatTime(item.updatedAt)} · {formatDate(item.updatedAt)}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-7" />
                      )}

                      {/* Customer */}
                      <div className={`${showHistory ? 'min-h-[65px] flex flex-col justify-center' : 'mt-3'}`}>
                        <p className="font-semibold text-black">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t(order.address.addressType)}, {order.address.formattedAddress}
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div className="p-4 pt-0 pb-6 bg-gray-300">
                {/* Order details block */}
                <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-4 text-sm">
                  <div className="flex items-center space-x-4">
                    <img
                      className="w-10 h-10 object-contain"
                      src={store?.logoImage}
                      alt="No image"
                    />
                    <div>
                      <p className="font-semibold text-black">{store?.storeName}</p>
                      <p className="text-[#666] text-xs">
                        {t('Order')} #{orderNumber} | {timestamp ? new Date(timestamp).toLocaleString() : t('Unknown time')} | {t(order.orderType)}
                      </p>
                    </div>
                  </div>

                  {/* Product header */}
                  <div className="grid grid-cols-3 gap-4 font-bold text-xs uppercase text-black border-b border-gray-300 pb-1">
                    <div>{t('Product')}</div>
                    <div className="text-center">{t('Qty')}</div>
                    <div className="text-right">{t('Price')}</div>
                  </div>

                  {/* Product list */}
                  {Array.isArray(order.cartItems) && order.cartItems.map((product, index) => {
                    const discountedItem = order.discountedItems?.find(
                      item =>
                        item.productId === product.productId && (item.variantId || null) === (product.variantId || null),
                    );

                    const originalSubtotal = Number(product.price) * Number(product.quantity);

                    const discountAmount = discountedItem
                      ? Number(discountedItem.discount)
                      : 0;

                    const discountedSubtotal = discountedItem
                      ? Number(discountedItem.discountedSubtotal)
                      : originalSubtotal;

                    const taxRate = Number(product.tax || 0);

                    const productTax = (discountedSubtotal * taxRate) / 100;

                    return (
                      <div
                        key={index}
                        className="text-xs text-[#555] pt-2 pb-3 border-b border-gray-300"
                      >
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            {i18n.language === 'fr'
                              ? product.productFrName
                              : product.productEnName}

                            {product.variantFrName && (
                              <div>
                                - {i18n.language === 'fr'
                                  ? product.variantFrName
                                  : product.variantEnName}
                              </div>
                            )}
                          </div>

                          <div className="text-center">
                            {product.quantity}
                          </div>

                          <div className="text-right">
                            {originalSubtotal.toFixed(2)}$
                          </div>
                        </div>

                        {/* Product coupon reduction */}
                        {discountAmount > 0 && (
                          <div className="flex justify-between mt-2 text-green-600">
                            <span>
                              {t('Coupon discount', {
                                defaultValue: 'Coupon discount',
                              })}
                            </span>
                            <span>
                              -{discountAmount.toFixed(2)}$
                            </span>
                          </div>
                        )}

                        {/* Product tax after discount */}
                        <div className="flex justify-between mt-2">
                          <span>
                            {t('taxes')}
                          </span>
                          <span>
                            {productTax.toFixed(2)}$
                          </span>
                        </div>
                      </div>
                    );
                  })}


                  {/* Totals */}
                  <div className="mt-4 space-y-2 border-t border-gray-300 pt-3 text-xs text-[#555]">

                    {/* Before coupon */}
                    {couponDiscount > 0 && (
                      <div className="flex justify-between">
                        <span>
                          {t('Subtotal before discount')}
                        </span>
                        <span>
                          {formatMoney(subtotalBeforeDiscount)}$
                        </span>
                      </div>
                    )}


                    {/* Coupon total */}
                    {couponDiscount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>
                          {t('Coupon discount', {
                            defaultValue: 'Coupon discount',
                          })}
                          {order?.coupon?.code
                            ? ` (${order.coupon.code})`
                            : ''}
                        </span>

                        <span>
                          -{formatMoney(couponDiscount)}$
                        </span>
                      </div>
                    )}


                    {/* Subtotal after discount */}
                    <div className="flex justify-between">
                      <span>
                        {t('Subtotal')}
                      </span>
                      <span>
                        {formatMoney(subtotalAfterDiscount)}$
                      </span>
                    </div>


                    {/* Taxes */}
                    <div className="flex justify-between">
                      <span>
                        {t('taxes')}
                      </span>
                      <span>
                        {formatMoney(totalTaxes)}$
                      </span>
                    </div>

                    {/* Grand total */}
                    <div className="flex justify-between font-bold text-black">
                      <span>
                        {t('grandTotal')}
                      </span>

                      <span>
                        {formatMoney(grandTotal)}$
                      </span>
                    </div>

                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default TrackOrder;
